/**
 * Production-Ready Event Bus
 * 
 * Real implementation with pub/sub, error handling, and async processing
 */

import { Logger } from '../logging/Logger';

export interface DomainEvent {
  aggregateId: string;
  eventType: string;
  occurredAt: Date;
  version?: number;
  metadata?: Record<string, any>;
  [key: string]: any;
}

export type EventHandler = (event: DomainEvent) => Promise<void> | void;

export interface EventSubscription {
  unsubscribe(): void;
}

/**
 * Production Event Bus with proper pub/sub
 */
export class EventBus {
  private static instance: EventBus;
  private subscribers = new Map<string, Set<EventHandler>>();
  private wildcardSubscribers = new Set<EventHandler>();
  private logger: Logger;
  private eventHistory: DomainEvent[] = [];
  private maxHistorySize = 1000;

  private constructor() {
    this.logger = Logger.getInstance().child({ component: 'EventBus' });
  }

  static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  /**
   * Publish an event to all subscribers
   */
  async publish(event: DomainEvent): Promise<void> {
    const startTime = Date.now();
    
    // Add to history for debugging/replay
    this.addToHistory(event);
    
    // Log the event
    this.logger.debug('Publishing event', {
      eventType: event.eventType,
      aggregateId: event.aggregateId,
      timestamp: event.occurredAt
    });

    // Get all handlers for this event type
    const handlers = this.getHandlersForEvent(event.eventType);
    
    // Execute handlers in parallel with error isolation
    const results = await Promise.allSettled(
      handlers.map(handler => this.executeHandler(handler, event))
    );

    // Log any failures
    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      this.logger.error(
        `Event handler failures for ${event.eventType}`,
        new Error('Handler failures'),
        {
          eventType: event.eventType,
          failureCount: failures.length,
          failures: failures.map((f: any) => f.reason?.message || f.reason)
        }
      );
    }

    const duration = Date.now() - startTime;
    this.logger.debug('Event published', {
      eventType: event.eventType,
      handlerCount: handlers.length,
      failureCount: failures.length,
      duration
    });
  }

  /**
   * Subscribe to a specific event type
   */
  subscribe(eventType: string, handler: EventHandler): EventSubscription {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set());
    }
    
    this.subscribers.get(eventType)!.add(handler);
    
    this.logger.debug('Subscriber added', { eventType });

    // Return subscription handle for cleanup
    return {
      unsubscribe: () => {
        const handlers = this.subscribers.get(eventType);
        if (handlers) {
          handlers.delete(handler);
          if (handlers.size === 0) {
            this.subscribers.delete(eventType);
          }
        }
        this.logger.debug('Subscriber removed', { eventType });
      }
    };
  }

  /**
   * Subscribe to all events (wildcard)
   */
  subscribeToAll(handler: EventHandler): EventSubscription {
    this.wildcardSubscribers.add(handler);
    
    this.logger.debug('Wildcard subscriber added');

    return {
      unsubscribe: () => {
        this.wildcardSubscribers.delete(handler);
        this.logger.debug('Wildcard subscriber removed');
      }
    };
  }

  /**
   * Get all handlers for an event type
   */
  private getHandlersForEvent(eventType: string): EventHandler[] {
    const handlers: EventHandler[] = [];
    
    // Add specific handlers
    const specificHandlers = this.subscribers.get(eventType);
    if (specificHandlers) {
      handlers.push(...specificHandlers);
    }
    
    // Add wildcard handlers
    handlers.push(...this.wildcardSubscribers);
    
    return handlers;
  }

  /**
   * Execute a handler with error isolation
   */
  private async executeHandler(
    handler: EventHandler,
    event: DomainEvent
  ): Promise<void> {
    try {
      await Promise.resolve(handler(event));
    } catch (error) {
      // Log but don't throw - isolate handler failures
      this.logger.error(
        'Event handler error',
        error as Error,
        {
          eventType: event.eventType,
          aggregateId: event.aggregateId
        }
      );
      throw error; // Re-throw for Promise.allSettled to catch
    }
  }

  /**
   * Add event to history for debugging/replay
   */
  private addToHistory(event: DomainEvent): void {
    this.eventHistory.push(event);
    
    // Trim history if too large
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory = this.eventHistory.slice(-this.maxHistorySize);
    }
  }

  /**
   * Get event history (for debugging)
   */
  getHistory(limit?: number): DomainEvent[] {
    if (limit) {
      return this.eventHistory.slice(-limit);
    }
    return [...this.eventHistory];
  }

  /**
   * Clear all subscribers (useful for testing)
   */
  clearAllSubscribers(): void {
    this.subscribers.clear();
    this.wildcardSubscribers.clear();
    this.logger.info('All subscribers cleared');
  }

  /**
   * Get subscriber statistics
   */
  getStats(): {
    eventTypes: number;
    totalHandlers: number;
    wildcardHandlers: number;
    historySize: number;
  } {
    let totalHandlers = 0;
    for (const handlers of this.subscribers.values()) {
      totalHandlers += handlers.size;
    }

    return {
      eventTypes: this.subscribers.size,
      totalHandlers,
      wildcardHandlers: this.wildcardSubscribers.size,
      historySize: this.eventHistory.length
    };
  }
}

/**
 * Domain Event Builder Helper
 */
export class DomainEventBuilder {
  static create(
    eventType: string,
    aggregateId: string,
    data: Record<string, any> = {}
  ): DomainEvent {
    return {
      eventType,
      aggregateId,
      occurredAt: new Date(),
      version: 1,
      ...data
    };
  }
}